import { Button, tokens } from "@blurb/codex-react";
import { useForm } from "@mantine/form";
import { createStyles } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex, RpiAlert, RpiCheckboxV2, useShowErrorToast, useShowSuccessToast } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";
import { RpaButtonPromo } from "../../../components/buttons/RpaButton";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { ConfirmationModalV2 } from "../../../components/modals/ConfirmationModalV2";
import { Body } from "../../../components/Typography";
import { getBraintreeDeviceData } from "../../../hooks/useBraintree";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { RpaDrawer, RpaDrawerProps } from "../../../rpi-core/drawer/RpaDrawer";
import { createRpiDrawerView } from "../../../rpi-core/drawer/RpiDrawer";
import { countrySelectData } from "../../../utils/country.util";
import { usStateSelectData } from "../../../utils/usState.util";
import { shouldRetry } from "../../../utils/query.util";
import { parsePaymentMethodError, requiredField } from "../../admin/billing/hooks/usePaymentFormSubmission";
import { FieldFrame } from "./FieldFrame";

const FORM_ID = "settings-edit-payment-method-form";

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
  },
  divider: {
    borderTop: `1px solid ${tokens.color.semantic.border.faint}`,
    width: "100%",
  },
});

export const SettingsEditPaymentMethodView = createRpiDrawerView<Pick<RpaDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { token } = useParams();
    const user = useAuthUser();

    const { data, isLoading, error } = GraphQL.useListPaymentMethodsQuery<
      GraphQL.ListPaymentMethodsQuery,
      { message: string }
    >(
      { customerPublicId: user.id! },
      { enabled: !!user.id && opened, refetchOnMount: true, retry: shouldRetry(onClose), cacheTime: 0 }
    );

    const paymentMethod = data?.listPaymentMethods.find((method) => method.token === token);
    const cardMissing = !!data && !paymentMethod;

    React.useEffect(() => {
      if (!token || cardMissing) onClose();
    }, [token, cardMissing, onClose]);

    return (
      <RpaDrawer title="Edit Payment Method" isLoading={isLoading} error={error} opened={opened} onClose={onClose}>
        {paymentMethod && (
          <EditPaymentMethodForm paymentMethod={paymentMethod} customerPublicId={user.id!} onClose={onClose} />
        )}
      </RpaDrawer>
    );
  }
);

interface EditPaymentMethodFormProps {
  paymentMethod: GraphQL.BraintreePaymentMethodResponse;
  customerPublicId: string;
  onClose: () => void;
}

type EditFormValues = {
  cardholderName: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  agreeToTerms: boolean;
};

const EditPaymentMethodForm: React.FC<EditPaymentMethodFormProps> = ({ paymentMethod, customerPublicId, onClose }) => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();
  const [countrySearchValue, setCountrySearchValue] = React.useState("");
  const [stateSearchValue, setStateSearchValue] = React.useState("");
  const [cardError, setCardError] = React.useState<string>();
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const editPaymentMethod = GraphQL.useEditPaymentMethodV2Mutation();
  const deletePaymentMethod = GraphQL.useDeletePaymentMethodMutation<{ message: string }>();
  const showSuccessToast = useShowSuccessToast();
  const showErrorToast = useShowErrorToast();

  const form = useForm<EditFormValues>({
    initialValues: {
      cardholderName: paymentMethod.cardholderName ?? "",
      streetAddress: paymentMethod.address ?? "",
      city: paymentMethod.city ?? "",
      state: paymentMethod.state ?? "",
      country: paymentMethod.countryIso2 ?? "US",
      agreeToTerms: true,
    },
    validate: {
      cardholderName: requiredField("Please enter a valid name"),
      streetAddress: requiredField("Please enter a valid street address"),
      city: requiredField("Please enter a valid town/city"),
      state: (value: string, values: EditFormValues) =>
        values.country === "US" && (value?.length ?? 0) === 0 ? "Please select a state" : null,
      country: requiredField("Please select a country"),
      agreeToTerms: (value: boolean) => (value ? null : "Please agree to the payment terms to continue"),
    },
  });

  const submitEdit = async (values: EditFormValues) => {
    setCardError(undefined);
    const deviceData = await getBraintreeDeviceData().catch(() => undefined);
    const result = await editPaymentMethod
      .mutateAsync({ input: buildEditInput(values, paymentMethod, deviceData) })
      .catch(() => undefined);

    if (!result) {
      showErrorToast("Something went wrong. Please try again.");
      return;
    }
    if (result.paymentMethodEditV2.__typename === "BraintreePaymentMethodRejection") {
      setCardError(parsePaymentMethodError(result.paymentMethodEditV2));
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["ListPaymentMethods"] });
    showSuccessToast("Your payment method was updated successfully!");
    onClose();
  };

  const handleSubmit = (values: EditFormValues) => void submitEdit(values);

  const handleDelete = async () => {
    deletePaymentMethod.reset();
    const result = await deletePaymentMethod
      .mutateAsync({ paymentMethodToken: paymentMethod.token!, customerPublicId })
      .catch(() => undefined);
    if (!result) return;

    await queryClient.invalidateQueries({ queryKey: ["ListPaymentMethods"] });
    setDeleteOpen(false);
    showSuccessToast("Your payment method was deleted successfully!");
    onClose();
  };

  return (
    <form id={FORM_ID} className={classes.body} onSubmit={form.onSubmit(handleSubmit)}>
      <RpaTextInput
        required
        label="Name on Card"
        data-testid="cardholderName"
        disabled={editPaymentMethod.isLoading}
        {...form.getInputProps("cardholderName")}
      />

      <ReadOnlyCardIdentity paymentMethod={paymentMethod} />

      <div className={classes.divider} />

      <RpaTextInput
        required
        label="Billing Street Address"
        data-testid="streetAddress"
        disabled={editPaymentMethod.isLoading}
        {...form.getInputProps("streetAddress")}
      />
      <RpaTextInput
        required
        label="Town/City"
        data-testid="city"
        disabled={editPaymentMethod.isLoading}
        {...form.getInputProps("city")}
      />
      {form.values.country === "US" && (
        <RpaDropdown
          required
          label="State"
          data-testid="state"
          placeholder="Select.."
          data={usStateSelectData}
          disabled={editPaymentMethod.isLoading}
          searchable
          clearable={false}
          filterDataOnExactSearchMatch
          searchValue={stateSearchValue}
          onSearchChange={setStateSearchValue}
          nothingFound="No states found"
          {...form.getInputProps("state")}
        />
      )}
      <RpaDropdown
        required
        label="Country"
        data={countrySelectData}
        disabled={editPaymentMethod.isLoading}
        searchable
        clearable={false}
        filterDataOnExactSearchMatch
        searchValue={countrySearchValue}
        onSearchChange={setCountrySearchValue}
        nothingFound="No countries found"
        {...form.getInputProps("country")}
      />

      <RpiCheckboxV2
        label="By saving your payment method, you agree to be charged for your orders daily."
        data-testid="termsCheckbox"
        disabled={editPaymentMethod.isLoading}
        {...form.getInputProps("agreeToTerms", { type: "checkbox", withError: true })}
      />

      <RpaDrawer.Footer>
        <div className={classes.footer}>
          {cardError && <RpiAlert data-testid="cardError">{cardError}</RpiAlert>}

          <RpaButtonPromo
            type="submit"
            form={FORM_ID}
            fullWidth
            isLoading={editPaymentMethod.isLoading}
            disabled={editPaymentMethod.isLoading}
            data-testid="savePaymentMethod"
          >
            Save Changes
          </RpaButtonPromo>
          <Button variant="text" intent="primary" type="button" onClick={onClose}>
            Cancel
          </Button>

          <div className={classes.divider} />

          <Button
            variant="text"
            intent="danger"
            type="button"
            data-testid="deletePaymentMethod"
            onClick={() => setDeleteOpen(true)}
          >
            Delete Payment Method
          </Button>
        </div>
      </RpaDrawer.Footer>

      <ConfirmationModalV2
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        isLoading={deletePaymentMethod.isLoading}
        error={deletePaymentMethod.error?.message}
        title="Delete Payment Method?"
        message="Are you sure you want to delete this payment method? You will need to add a new one to continue placing orders."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loadingLabel="Deleting..."
        confirmIntent="danger"
      />
    </form>
  );
};

const ReadOnlyCardIdentity: React.FC<{ paymentMethod: GraphQL.BraintreePaymentMethodResponse }> = ({
  paymentMethod,
}) => (
  <FieldFrame data-testid="payment-method-card-identity">
    <Flex.Row justify="space-between" align="center" fullWidth>
      <Body>Credit Card</Body>
      <Flex.Row gap={16} align="center">
        {paymentMethod.imageUrl && <img src={paymentMethod.imageUrl} alt={paymentMethod.name ?? "Card"} height={20} />}
        <Body>{paymentMethod.maskedCCNumber ?? `– ${paymentMethod.last4Digits}`}</Body>
        {paymentMethod.expiryDate && <Body>{`Exp ${paymentMethod.expiryDate}`}</Body>}
      </Flex.Row>
    </Flex.Row>
  </FieldFrame>
);

function buildEditInput(
  values: EditFormValues,
  paymentMethod: GraphQL.BraintreePaymentMethodResponse,
  deviceData: string | undefined
): GraphQL.BraintreePaymentMethodEditInput {
  return {
    token: paymentMethod.token!,
    cardholderName: values.cardholderName,
    streetAddress: values.streetAddress,
    city: values.city,
    state: values.state,
    country: values.country,
    makeDefault: paymentMethod.isDefault,
    deviceDataFromClient: deviceData,
  };
}
